/**
 * 게시판 모듈 Context
 *
 * - 현재 워크스페이스의 게시판 목록
 * - 선택된 게시판/게시글 상태
 * - CRUD 액션 래퍼
 */
import { createContext, useContext } from 'react';
import { Board, Post } from '../models/Board';

export interface IBoardContext {
  /** 현재 워크스페이스의 게시판 목록 */
  boards: Board[];
  setBoards: (boards: Board[]) => void;

  /** 현재 선택된 게시판 */
  selectedBoard: Board | null;
  setSelectedBoard: (board: Board | null) => void;

  /** 현재 선택된 게시글 (오버레이 표시) */
  selectedPost: Post | null;
  setSelectedPost: (post: Post | null) => void;

  /** 게시글 목록 */
  posts: Post[];
  setPosts: (posts: Post[]) => void;

  /** 게시글 총 개수 (페이지네이션) */
  totalPosts: number;
  setTotalPosts: (n: number) => void;

  /** 로딩 상태 */
  loading: boolean;
  setLoading: (b: boolean) => void;

  /** 게시판 목록 새로고침 */
  refreshBoards: () => Promise<void>;

  /** 게시글 목록 새로고침 */
  refreshPosts: (boardId: string, page?: number) => Promise<void>;

  /** 검색 쿼리 */
  searchQuery: string;
  setSearchQuery: (q: string) => void;

  /** 즐겨찾기(핀) 게시판 ID 목록 — LeftMenuBoard 와 BoardDashboard 가 공유.
   *  토글 시 두 곳이 동시에 re-render → 실시간 반영. */
  pinnedIds: string[];
  /** 즐겨찾기 토글 — 서버 반영 후 상태 갱신, 변경된 상태(true=pin) 반환 */
  togglePin: (boardId: string) => Promise<boolean>;

  /** 에러 상태 */
  error: string | null;
}

export const BoardContext = createContext<IBoardContext>({
  boards: [],
  setBoards: () => {},
  selectedBoard: null,
  setSelectedBoard: () => {},
  selectedPost: null,
  setSelectedPost: () => {},
  posts: [],
  setPosts: () => {},
  totalPosts: 0,
  setTotalPosts: () => {},
  loading: false,
  setLoading: () => {},
  refreshBoards: async () => {},
  refreshPosts: async () => {},
  searchQuery: '',
  setSearchQuery: () => {},
  pinnedIds: [],
  togglePin: async () => false,
  error: null,
});

export const useBoardContext = () => useContext(BoardContext);
